export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

// Type for metadata - allows common types but not literally any
export type LogMetadata = Record<
  string,
  string | number | boolean | Date | null | undefined
>;

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: string;
  metadata?: LogMetadata;
  error?: Error;
}

export interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  formatForProduction: boolean;
  context?: string;
}

export class Logger {
  private config: LoggerConfig;

  constructor(config: LoggerConfig) {
    this.config = config;
  }

  static createContextLogger(
    context: string,
    baseConfig: LoggerConfig
  ): Logger {
    return new Logger({
      ...baseConfig,
      context,
    });
  }

  createChildLogger(context: string): Logger {
    return new Logger({
      ...this.config,
      context,
    });
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = entry.timestamp;
    const levelName = LogLevel[entry.level];
    const context = entry.context ? `[${entry.context}]` : '';
    const metadata = entry.metadata ? ` ${JSON.stringify(entry.metadata)}` : '';

    if (this.config.formatForProduction) {
      return JSON.stringify({
        timestamp,
        level: levelName,
        message: entry.message,
        context: entry.context,
        metadata: entry.metadata,
        error: entry.error
          ? {
              name: entry.error.name,
              message: entry.error.message,
              stack: entry.error.stack,
            }
          : undefined,
      });
    }

    return `${timestamp} ${levelName} ${context} ${entry.message}${metadata}`;
  }

  private log(
    level: LogLevel,
    message: string,
    metadata?: LogMetadata,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: this.config.context,
      metadata,
      error,
    };

    if (this.config.enableConsole) {
      const formattedMessage = this.formatMessage(entry);

      switch (level) {
        case LogLevel.DEBUG:
          console.debug(formattedMessage);
          break;
        case LogLevel.INFO:
          console.info(formattedMessage);
          break;
        case LogLevel.WARN:
          console.warn(formattedMessage);
          if (error) console.warn(error);
          break;
        case LogLevel.ERROR:
          console.error(formattedMessage);
          if (error) console.error(error);
          break;
      }
    }
  }

  debug(message: string, metadata?: LogMetadata): void {
    this.log(LogLevel.DEBUG, message, metadata);
  }

  info(message: string, metadata?: LogMetadata): void {
    this.log(LogLevel.INFO, message, metadata);
  }

  warn(message: string, metadata?: LogMetadata, error?: Error): void {
    this.log(LogLevel.WARN, message, metadata, error);
  }

  error(message: string, metadata?: LogMetadata, error?: Error): void {
    this.log(LogLevel.ERROR, message, metadata, error);
  }

  // Convenience methods for common use cases
  userAction(action: string, metadata?: LogMetadata): void {
    this.info('User Action', { action, ...metadata });
  }

  performance(
    operation: string,
    duration: number,
    metadata?: LogMetadata
  ): void {
    this.info('Performance', {
      operation,
      duration: `${duration}ms`,
      ...metadata,
    });
  }

  // Method to time operations
  time(operation: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.performance(operation, duration);
    };
  }
}
